import { useState, useEffect } from 'react';
import { getTemplate, getAvailableTemplates } from '../templates/registry';

interface UseLandingPageTemplateResult {
  html: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to load landing page template from environment variable
 * Uses VITE_LANDING_PAGE_TEMPLATE from .env
 * Loads templates from shared/src/templates dynamically
 * Falls back to default (null) if not configured
 */
export const useLandingPageTemplate = (): UseLandingPageTemplateResult => {
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      // Get template path from environment variable
      const templatePath = import.meta.env.VITE_LANDING_PAGE_TEMPLATE;

      console.log('[useLandingPageTemplate] VITE_LANDING_PAGE_TEMPLATE:', templatePath);
      console.log('[useLandingPageTemplate] Available templates:', getAvailableTemplates());

      // If no template is configured, return null (use default)
      if (!templatePath) {
        console.log('[useLandingPageTemplate] No template configured, using default');
        setHtml(null);
        setError(null);
        setLoading(false);
        return;
      }

      // Get template from registry
      const templateContent = getTemplate(templatePath);
      
      console.log('[useLandingPageTemplate] Template content:', templateContent ? 'Found' : 'Not found');
      
      if (templateContent) {
        setHtml(templateContent);
        setError(null);
        console.log('[useLandingPageTemplate] Landing page template loaded successfully:', templatePath);
      } else {
        throw new Error(`Template not found in registry: ${templatePath}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('[useLandingPageTemplate] Error loading landing page template:', message);
      setError(message);
      // Fall back to default (null) on error
      setHtml(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return { html, loading, error };
};
